import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';

import { UNCONFIRMED_CLIENT_ROLE } from '@/features/auth/hooks/use-is-unconfirmed-client';
import { useWhoami } from '@/features/auth/hooks/use-whoami-query';

import { Typography } from '@/components/typography/typography';

import { router } from '@/lib/router-config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/lib/shadcn/components/ui/dialog';
import { LoadingMainButton } from '@/lib/shadcn/components/ui/loading-button/loading-button';
import { Stack } from '@/lib/shadcn/components/ui/stack/stack';

import { useGetConsentStatus } from '../hooks/use-get-consent-status';
import { useLogConsent } from '../hooks/use-log-consent';

function useAppPathname() {
  const [pathname, setPathname] = useState(
    () => router.state.location.pathname ?? globalThis.location?.pathname ?? '',
  );

  useEffect(() => {
    return router.subscribe('onResolved', ({ toLocation }) => {
      setPathname(toLocation.pathname);
    });
  }, []);

  return pathname;
}

export function LegalDocumentsConsentModal() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pathname = useAppPathname();
  const isDocsPage = pathname === '/docs' || pathname.startsWith('/docs/');
  const { data: whoamiData } = useWhoami({ params: {} });
  const shouldRequestConsent =
    !isDocsPage &&
    whoamiData !== undefined &&
    !whoamiData.auth.roles.includes(UNCONFIRMED_CLIENT_ROLE);

  const { data: consentStatus } = useGetConsentStatus({
    params: {},
    enabled: shouldRequestConsent,
  });

  const { mutate: logConsent, isPending } = useLogConsent({
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes(useGetConsentStatus.method) ||
          query.queryKey.includes('user:get-document-consent-status') ||
          query.queryKey.includes('bonuses:get-bonuses-by-user-id') ||
          query.queryKey.includes('bonuses:get-available-bonuses'),
      });
    },
  });

  const handleAccept = () => {
    if (!consentStatus) return;

    logConsent({
      action: 'grant',
      formType: 'legal_update',
      signedDocuments: Object.keys(consentStatus.documentVersions),
    });
  };

  const isOpen = shouldRequestConsent ? Boolean(consentStatus?.required) : false;

  return (
    <Dialog open={isOpen}>
      <DialogContent className="w-[calc(100%-32px)] max-w-[360px] gap-unit-8">
        <Stack gap={4}>
          <DialogTitle asChild>
            <Typography.h5 className="text-slate-500">
              {t('legal-documents-consent.title')}
            </Typography.h5>
          </DialogTitle>
          <DialogDescription asChild>
            <Typography.p3 className="text-slate-400">
              <Trans
                i18nKey="legal-documents-consent.text"
                components={{
                  docs: (
                    <a
                      className="text-teal-500 underline underline-offset-2"
                      href="/docs/"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </Typography.p3>
          </DialogDescription>
        </Stack>
        <LoadingMainButton className="w-full" loading={isPending} onClick={handleAccept}>
          {t('legal-documents-consent.accept')}
        </LoadingMainButton>
      </DialogContent>
    </Dialog>
  );
}
